import { Component, EventEmitter, OnInit, OnDestroy, Output, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-trial-balance',
    templateUrl: './trial-balance.component.html',
    styleUrls: ['./trial-balance.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class TrialBalanceComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();
  private isInitialized = false;
  private isAuditLoaded = false;
  private sortReverse = false;
  private sortColumn = '';

  title = 'Trial Balance';
  currentTabName = 'Book Adjustment';
  minimise = false;
  showFormLoader = true;

  activeTab = 0;
  tabs = [
    { id: 0, name: 'Book Adjustment' },
    { id: 1, name: 'Repoint' },
    { id: 2, name: 'Audit' }
  ];

  baseURLs: any = {};
  globalService: any = {};
  USER_SETTINGS: any = {};
  GENERAL_CONFIG: any = {};
  filterParams: any = {};
  rowData: any = {};

  baseUrlsJson = '{}';
  headerDataJson = '{}';
  rowDataJson = '{}';
  filterParamsJson = '{}';

  auditRows: any[] = [];
  auditColumns: string[] = [];
  noAuditRecords = false;
  taxYearBegin = '';
  taxYearEnd = '';

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private cdr: ChangeDetectorRef,
    private http: HttpClient
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => { this.applyWorkspaceContext(); this.tryInit(); }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => { this.applyWorkspaceContext(); this.tryInit(); }));
    this.tryInit();
  }

  ngOnDestroy(): void { this.subs.unsubscribe(); }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = header.globalParams || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.baseUrlsJson = JSON.stringify(this.baseURLs);
    this.headerDataJson = JSON.stringify(header);
    this.rowDataJson = JSON.stringify(this.rowData);
    this.filterParamsJson = JSON.stringify(this.filterParams);
    this.cdr.detectChanges();
  }

  private tryInit(): void {
    if (this.isInitialized) {
      return;
    }

    if (
      !this.GENERAL_CONFIG?.base_url ||
      !this.rowData ||
      Object.keys(this.rowData).length === 0 ||
      !this.filterParams ||
      Object.keys(this.filterParams).length === 0
    ) {
      return;
    }

    this.isInitialized = true;
    this.setupEntityData();
    this.showFormLoader = false;
    this.cdr.detectChanges();
  }

  private setupEntityData(): void {
    this.taxYearBegin = this.formatDate(this.rowData?.TAX_YEAR_BEGIN);
    this.taxYearEnd = this.formatDate(this.rowData?.TAX_YEAR_END);
  }

  private formatDate(value: any): string {
    if (!value) {
      return '';
    }
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) {
      return '';
    }
    return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
  }

  onTabChange(tabId: number): void {
    this.activeTab = tabId;
    const tab = this.tabs.find((t) => t.id === tabId);
    this.currentTabName = tab ? tab.name : this.currentTabName;

    if (tabId === 2 && !this.isAuditLoaded) {
      this.loadAuditSummary();
    }
  }

  private loadAuditSummary(): void {
    this.showFormLoader = true;
    const ssoId = this.USER_SETTINGS?.user?.sso_id || '';
    const params: any = {
      action_code: 'ssg8w0',
      sso_id: ssoId,
      local_acct_key: this.rowData.LOCAL_ACCT_KEY,
      cc_key: this.rowData.COMBINATION_KEY
    };
    const httpParams = new HttpParams({ fromObject: params });
    this.http.get<any>(`${this.GENERAL_CONFIG.base_url}/loadJsonObject`, { params: httpParams, withCredentials: true }).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (data: any) => {
          this.auditRows = Array.isArray(data?.jsonObject) ? data.jsonObject : [];
          this.auditColumns = ['Updated On', 'Repoint Desc', 'System Acct', 'Amount', 'DML Type', 'Old New Flag', 'Updated By'];
          this.noAuditRecords = this.auditRows.length === 0;
          this.isAuditLoaded = true;
          this.showFormLoader = false;
          this.cdr.detectChanges();
        },
        () => {
          this.auditRows = [];
          this.auditColumns = ['Updated On', 'Repoint Desc', 'System Acct', 'Amount', 'DML Type', 'Old New Flag', 'Updated By'];
          this.noAuditRecords = true;
          this.isAuditLoaded = true;
          this.showFormLoader = false;
          this.cdr.detectChanges();
        }
      );
  }

  onChildGridUpdate(event: any): void {
    const detail = event?.detail ?? event;
    if (detail === 'refresh') {
      this.requestGridUpdate.emit('refresh');
      return;
    }
    this.requestGridUpdate.emit('cancel');
  }

  sortAudit(column: string): void {
    if (this.sortColumn === column) {
      this.sortReverse = !this.sortReverse;
    } else {
      this.sortColumn = column;
      this.sortReverse = false;
    }
    this.auditRows = [...this.auditRows].sort((a, b) => {
      const va = a?.[column];
      const vb = b?.[column];
      if (va === vb) {
        return 0;
      }
      if (va === undefined || va === null) {
        return this.sortReverse ? 1 : -1;
      }
      if (vb === undefined || vb === null) {
        return this.sortReverse ? -1 : 1;
      }
      return this.sortReverse
        ? String(vb).localeCompare(String(va))
        : String(va).localeCompare(String(vb));
    });
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}
